import type { TreeSelectProps } from "antd";
import { TreeSelect } from "antd";
import { useMemo } from "react";
import type { Department } from "@/modules/departments/types";
import { buildTree } from "@/modules/departments/utils/buildTree";

/**
 * Tanlangan bo'limning AJDODLARI (`path` = `/‹ajdod›/…/‹o'zi›/`) — o'zi
 * tashlanadi (uning farzandlari so'ralmagan holda ochilmasin), to'plamda
 * yo'q ajdod ham (`restrictToIds` kesgan bo'lsa `buildTree` qatorni ildiz
 * qilib ko'rsatadi). `path?.` — ba'zi test fixture'lari qisman obyekt.
 */
function ancestorKeysOf(value: unknown, departments: Department[]): string[] {
	if (typeof value !== "string") {
		return [];
	}
	const row = departments.find((department) => department.id === value);
	const present = new Set(departments.map((department) => department.id));
	return (row?.path?.split("/").filter(Boolean).slice(0, -1) ?? []).filter((id) => present.has(id));
}

/**
 * ⚠️ **Umumiy komponent — P5 (rol/foydalanuvchi scope tanlash) va P6
 * (topshiriq ko'p-tashkilot biriktirish) ham AYNAN shu komponentni
 * ishlatadi** (`2026-09-12-p4-tashkilot-hududlar-design.md` §3). Interfeys
 * (`departments`, `restrictToIds`) qasddan barqaror — o'zgartirsangiz ikkala
 * iste'molchini ham tekshiring.
 *
 * `buildTree()` QAYTA YOZILMAYDI — `Tree` uchun tayyorlangan shakl
 * (`{key, title, children}`) `fieldNames` orqali to'g'ridan-to'g'ri
 * `TreeSelect`ga beriladi.
 */
export interface DepartmentTreeSelectProps
	extends Omit<TreeSelectProps, "treeData" | "fieldNames"> {
	departments: Department[];
	/** Berilsa, faqat shu ID'lar (va ularning avlodlari) ko'rsatiladi — department-scope uchun. */
	restrictToIds?: string[];
}

export function DepartmentTreeSelect({
	departments,
	restrictToIds,
	...rest
}: DepartmentTreeSelectProps) {
	const filtered = useMemo(
		() => (restrictToIds ? departments.filter((d) => restrictToIds.includes(d.id)) : departments),
		[departments, restrictToIds]
	);

	/**
	 * ⚠️ Daraxt standart YIG'IQ (~1200 tugun), shuning uchun qiymat bilan
	 * ochilgan select tanlangan tugunning yo'lini O'ZI ochishi shart — aks
	 * holda chuqur bo'lim yashirin qoladi: rc-tree-select ochilishdagi
	 * `scrollTo({ key })` yig'iq kalitni tekis ro'yxatda topmaydi va jimgina
	 * hech narsa qilmaydi, bir xil nomli barg («Kassa») esa ko'p shoxda bor.
	 *
	 * `treeDefaultExpandedKeys` — BOSHQARILMAYDIGAN (initial-only), ataylab.
	 * OptionList uni `useState` bilan o'qiydi, lekin popap BIRINCHI OCHILGANDA
	 * mount bo'ladi (komponent mount'ida emas) — ya'ni qiymat birinchi ochilish
	 * paytidagisi olinadi: `destroyOnHidden` modallar (FormModal,
	 * AssignRoleModal) har ochilishda qayta mount qiladi, `ZonesPage` filtri va
	 * `OrgAssigneePicker` esa `undefined` dan boshlanadi va qiymat faqat shu
	 * select orqali o'zgaradi (switcher bilan ochilgan yo'l ichki holatda
	 * qoladi). Boshqariladigan `treeExpandedKeys` ATAYLAB olinmadi: u
	 * `OptionList`ning `searchExpandedKeys`ini butunlay chetlab o'tadi va
	 * qidiruvda mos shoxlar avtomatik ochilmay qo'yardi.
	 */
	const defaultExpandedKeys = useMemo(
		() => ancestorKeysOf(rest.value, filtered),
		[rest.value, filtered]
	);

	return (
		<TreeSelect
			allowClear
			treeData={buildTree(filtered)}
			treeDefaultExpandedKeys={defaultExpandedKeys}
			fieldNames={{ label: "title", value: "key", children: "children" }}
			// ⚠️ antd 6 da `treeNodeFilterProp`/`filterTreeNode` ESKIRGAN —
			// `showSearch={{ treeNodeFilterProp, filterTreeNode }}` shakli
			// (C2 Task 7a).
			showSearch={{
				treeNodeFilterProp: "title",
				filterTreeNode: (input, node) =>
					String(node.title).toLowerCase().includes(input.toLowerCase()),
			}}
			{...rest}
		/>
	);
}
